import React from "react";
import ReactSelect, { components } from "react-select";
import AsyncSelect from "react-select/lib/Async";
import Field from "../Common/Field";
import Icon from "../../Common/Icon";
import throttle from "../../../utils/throttle";
import SelectSources from "./SelectSources";

export default class Select extends Field {
  handleChange = (selectedOptions) => {
    if (this.props.config.readOnly) return;
    if (
      selectedOptions === null ||
      (selectedOptions && selectedOptions.length === 0)
    ) {
      this.setValue("");
      return;
    }

    if (selectedOptions instanceof Array) {
      this.setValue(selectedOptions.map((option) => option.value));
    } else {
      const source = this.props.config.optionsSource;
      if (source) {
        const optionsSourceBaseUrl = this.props.config.optionsSourceBaseUrl;
        try {
          SelectSources[source].convertSelectValueToValue(
            selectedOptions,
            (value) => this.setValue(value),
            optionsSourceBaseUrl
          );
        } catch (ex) {
          console.warn(
            `The select-field value could not be saved. The "optionsSource" of field "${this.props.config.name}" is invalid.`
          );
        }
      } else {
        this.setValue(selectedOptions.value);
      }
    }
  };

  setValue(value) {
    const { formikForm, config } = this.props;
    formikForm.setFieldValue(config.name, value);
  }

  onSelectFocus = (e) => {
    const { formikForm, config } = this.props;
    formikForm.setFieldError(config.name);
    this.onFocus(e);
  };

  onSelectBlur = (e, target) => {
    if (this.props.config.readOnly) return;
    e.target = target;
    this.onBlur(e);
  };

  loadOptions = (inputValue, callback) => {
    const source = this.props.config.optionsSource;
    if (source) {
      const optionsSourceBaseUrl = this.props.config.optionsSourceBaseUrl;
      const optionsSourceLabelPrefix =
        this.props.config.optionsSourceLabelPrefix;
      const options = this.props.config.optionsSourceOptions;
      try {
        if (options && options.offset && inputValue.length < options.offset) {
          callback([]);
        } else {
          SelectSources[source].search(
            inputValue,
            callback,
            optionsSourceBaseUrl,
            optionsSourceLabelPrefix
          );
        }
      } catch (ex) {
        console.warn(
          `No search results found for the select-field. The "optionsSource" of field "${this.props.config.name}" is invalid.`
        );
      }
    } else {
      callback([]);
    }
  };

  getOptions() {
    const { formikForm } = this.props;
    let { options, filteredOptions = [] } = this.props.config;

    if (filteredOptions.length) {
      const matchingFilter = filteredOptions.find((definition) => {
        const filter = definition.filter || {};
        if (
          filter.rule === "equals" &&
          formikForm.values[this.encodeDots(filter.name)] === filter.value
        ) {
          return true;
        }
        return false;
      });
      return matchingFilter ? matchingFilter.options || [] : [];
    } else {
      return options;
    }
  }

  encodeDots(name) {
    return name.replace(/\./g, "%2E");
  }

  getSelectValue() {
    const { name, isMulti, optionsSource, optionsSourceLabelPrefix } =
      this.props.config;
    const value = this.props.formikField.value;
    let selectValue = "";
    const options = this.getOptions();

    if (isMulti) {
      selectValue = value
        ? value.map((value) => options.find((option) => option.value === value))
        : [];
    } else {
      const source = optionsSource;
      if (source) {
        try {
          selectValue = typeof value === "string" ? JSON.parse(value) : value;
        } catch (ex) {}
        try {
          selectValue = SelectSources[source].convertValueToSelectValue(
            selectValue,
            optionsSourceLabelPrefix
          );
        } catch (ex) {
          console.warn(
            `The selected option cannot be converted to a value. The "optionsSource" of field "${name}" is invalid.`
          );
        }
      } else {
        selectValue = options.find((option) => option.value === value) || null;

        //If value is not in the list of options. Then delete the value
        if (!selectValue && value) {
          this.setValue("");
        }
      }
    }
    return selectValue;
  }

  renderOnce() {
    const { config } = this.props;
    const {
      optionsSource,
      hideDropdownIndicator = false,
      loadingMessage = "Laden...",
    } = config;

    this.Template = optionsSource ? AsyncSelect : ReactSelect;
    let optionsAttr = {};
    if (optionsSource) {
      optionsAttr = {
        loadOptions: throttle(this.loadOptions, 1000),
        cacheOptions: true,
      };
    } else {
      optionsAttr = {
        options: this.getOptions(),
      };
    }
    this.optionsAttr = optionsAttr;

    let selectComponents = {
      DropdownIndicator: hideDropdownIndicator
        ? () => null
        : (props) => {
            //Todo 2018-9-24: Check on isOpen or some propery like that instead of isFocused
            return (
              <components.DropdownIndicator {...props}>
                {
                  <Icon
                    iconName={`${
                      props.isFocused ? "chevron-up" : "chevron-down"
                    }`}
                  />
                }
              </components.DropdownIndicator>
            );
          },
      ClearIndicator: (props) => {
        return (
          <components.ClearIndicator {...props}>
            <Icon iconName="cross" title="Verwijder geselecteerde keuze" />
          </components.ClearIndicator>
        );
      },
      LoadingIndicator: (props) => {
        return (
          <components.LoadingIndicator {...props}>
            <Icon
              className="select__loading-indicator"
              iconName="loading-light"
              title="Resultaten worden geladen"
            />
          </components.LoadingIndicator>
        );
      },
      LoadingMessage: (props) => {
        return (
          <components.LoadingMessage {...props}>
            <div className="select__loading-message">{loadingMessage}</div>
          </components.LoadingMessage>
        );
      },
    };

    if (optionsSource && SelectSources[optionsSource].components) {
      selectComponents = Object.assign(
        {},
        selectComponents,
        SelectSources[optionsSource].components(config)
      );
    }

    if (this.props.config.isMulti) {
      this.setMultiSelectProps(optionsAttr, selectComponents);
    }

    this.selectComponents = selectComponents;
  }

  setMultiSelectProps(optionsAttr, selectComponents) {
    const { config } = this.props;
    const { placeholder, multiInputText } = config;
    optionsAttr.hideSelectedOptions = false;
    optionsAttr.closeMenuOnSelect = false;

    selectComponents.ValueContainer = function (props, a, b) {
      const { children, getValue } = props;

      var totalSelected = getValue().length;
      const text = totalSelected
        ? (multiInputText || "{0} selected").replace("{0}", totalSelected)
        : placeholder;

      return (
        <components.ValueContainer {...props}>
          <div className="select__single-value">{text}</div>
          {React.cloneElement(children[1])}
        </components.ValueContainer>
      );
    };
  }

  renderInput() {
    const { config, isRequired } = this.props;
    const {
      label,
      name,
      placeholder,
      optionsSource,
      noOptionsMessage = "Geen opties gevonden",
      filteredOptions,
      isMulti = false,
      isSearchable = false,
      isClearable = false,
      readOnly,
      disabled,
    } = config;
    const value = this.getSelectValue();

    if (
      !this.Template ||
      filteredOptions // Render again because the options could have changed
    ) {
      this.renderOnce();
    }
    const Template = this.Template;

    const props = this.props;
    const thisOnSelectBlur = this.onSelectBlur;
    return (
      <Template
        name={name}
        value={value}
        styles={{
          clearIndicator: () => undefined,
          container: () => undefined,
          control: () => undefined,
          dropdownIndicator: () => undefined,
          groupHeading: () => undefined,
          indicatorsContainer: () => undefined,
          indicatorSeparator: () => undefined,
          input: () => undefined,
          loadingIndicator: () => undefined,
          loadingMessage: () => undefined,
          menu: () => undefined,
          menuList: () => undefined,
          multiValue: () => undefined,
          multiValueLabel: () => undefined,
          multiValueRemove: () => undefined,
          noOptionsMessage: () => undefined,
          option: () => undefined,
          placeholder: () => undefined,
          singleValue: () => undefined,
          valueContainer: () => undefined,
        }}
        components={this.selectComponents}
        //onClearIndicatorMouseDown={(props) => {
        //  console.log(props)
        //}}
        placeholder={placeholder}
        className={"field__input field__input--select select__container"}
        classNamePrefix="select"
        onChange={this.handleChange}
        onBlur={function (e) {
          thisOnSelectBlur(e, this, props);
        }}
        onFocus={this.onSelectFocus}
        isMulti={isMulti}
        isSearchable={isSearchable && !readOnly}
        isClearable={isClearable}
        openOnClick={false}
        openMenuOnClick={false}
        openMenuOnFocus={!optionsSource || !readOnly}
        blurInputOnSelect={false}
        noOptionsMessage={() => noOptionsMessage}
        aria-label={label}
        aria-required={!!isRequired}
        isDisabled={disabled}
        {...this.optionsAttr}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/FormTemplates/Field/Select.js